import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  LEAVE_REQUEST_STATUS_COLORS,
  LEAVE_REQUEST_STATUS_LABELS,
  LEAVE_TYPE_COLORS,
  LEAVE_TYPE_LABELS,
} from '../../containers/attendancemng/AttendMngContainer';

// bito에는 ApprovalV2(상세 액션 이력)가 없어 "결재 단계"만 보여준다. 단계는
// COM_APPR_DET(DOCY_TYP='HR_LEAVE') 행 그대로: docuStep/docuUsrNm/docuYn(S=대기,Y=승인,N=반려)/
// docuTxt(의견)/docuDt.
const STEP_STATUS_LABELS = {
  S: '대기',
  Y: '승인',
  N: '반려',
};

const LeaveRequestDetailDialog = ({ open, loading, detail, onClose }) => {
  const info = detail?.info;
  const steps = detail?.steps || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>휴가 신청 상세</DialogTitle>
      <DialogContent dividers>
        {!info ? (
          <Typography variant="body2" color="text.secondary">
            {loading ? '상세 정보를 불러오는 중입니다.' : '표시할 휴가 신청 정보가 없습니다.'}
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>{info.reqNo}</Typography>
                <Chip
                  label={LEAVE_REQUEST_STATUS_LABELS[info.reqStatus] || info.reqStatus}
                  color={LEAVE_REQUEST_STATUS_COLORS[info.reqStatus] || 'default'}
                  size="small"
                />
                <Chip
                  label={LEAVE_TYPE_LABELS[info.leaveTyp] || info.leaveTyp}
                  color={LEAVE_TYPE_COLORS[info.leaveTyp] || 'default'}
                  size="small"
                  variant="outlined"
                />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">신청자</Typography>
                  <Typography variant="body2">{info.usrNm} {info.dptNm ? `(${info.dptNm})` : ''}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">현재 결재자</Typography>
                  <Typography variant="body2">{info.currentActorNm || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">기간</Typography>
                  <Typography variant="body2">
                    {dayjs(info.leaveDtSt).format('YYYY-MM-DD')} ~ {dayjs(info.leaveDtEd).format('YYYY-MM-DD')}
                    {info.leaveTyp === 'ANNUAL_HOURLY' && info.leaveStartTm ? ` (${info.leaveStartTm} ~, ${info.leaveHours}시간)` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">일수</Typography>
                  <Typography variant="body2">{info.leaveDays}일</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">상신일</Typography>
                  <Typography variant="body2">
                    {info.submittedAt ? dayjs(info.submittedAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">최종 반영 근태 UUID</Typography>
                  <Typography variant="body2">{info.finalLeaveUuid || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">사유</Typography>
                  <Typography variant="body2">{info.rmk || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                결재 단계
              </Typography>
              <List dense disablePadding>
                {steps.map((step) => (
                  <ListItem key={step.apprUuid} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={(
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {step.docuStep}차 결재
                          </Typography>
                          <Chip
                            label={STEP_STATUS_LABELS[step.docuYn] || step.docuYn}
                            size="small"
                            color={step.docuYn === 'Y' ? 'success' : step.docuYn === 'N' ? 'error' : 'default'}
                            variant={step.docuYn === 'S' ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      )}
                      secondary={(
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            결재자: {step.docuUsrNm || step.docuUsr || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            처리일: {step.docuDt || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            의견: {step.docuTxt || '-'}
                          </Typography>
                        </Stack>
                      )}
                    />
                  </ListItem>
                ))}
                {steps.length === 0 && (
                  <Typography variant="body2" color="text.secondary">결재 단계 정보가 없습니다.</Typography>
                )}
              </List>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveRequestDetailDialog;
